import { useState, useCallback, useMemo } from 'react'
import type { DataTableSearchProps, SearchVariant } from '../components/data-table/DataTableSearch'

export interface UseResourceSearchOptions {
  /** Placeholder text for search input */
  placeholder?: string
  /** Visual variant of the search input */
  variant?: SearchVariant
}

export interface UseResourceSearchResult {
  /** Current search value */
  searchValue: string
  /** Update search value */
  setSearchValue: (value: string) => void
  /** Props to spread on DataTableSearch in ResourcePageLayout.headerActions */
  searchProps: Omit<DataTableSearchProps, 'className'>
  /** Props to spread on list component (searchValue, onSearchChange) */
  listSearchProps: {
    searchValue: string
    onSearchChange: (value: string) => void
  }
}

/**
 * Hook to manage search state for resource pages.
 * Returns props to spread on both the search input and the list component.
 *
 * @example
 * ```tsx
 * function Page() {
 *   const { searchProps, listSearchProps } = useResourceSearch({
 *     placeholder: 'Search members...',
 *   })
 *
 *   return (
 *     <ResourcePageLayout
 *       title="Members"
 *       headerActions={<DataTableSearch {...searchProps} />}
 *     >
 *       <WorkspaceMembersList {...listSearchProps} />
 *     </ResourcePageLayout>
 *   )
 * }
 * ```
 */
export function useResourceSearch(
  options: UseResourceSearchOptions = {}
): UseResourceSearchResult {
  const { placeholder = 'Search...', variant = 'with-icon' } = options

  const [searchValue, setSearchValue] = useState('')

  const handleChange = useCallback((value: string) => {
    setSearchValue(value)
  }, [])

  const searchProps = useMemo(
    () => ({
      value: searchValue,
      onChange: handleChange,
      placeholder,
      variant,
    }),
    [searchValue, handleChange, placeholder, variant]
  )

  const listSearchProps = useMemo(
    () => ({
      searchValue,
      onSearchChange: handleChange,
    }),
    [searchValue, handleChange]
  )

  return {
    searchValue,
    setSearchValue,
    searchProps,
    listSearchProps,
  }
}
